import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCountry } from '../pages/CountryContext';
import { policyDropdownItems } from "../utils/regionData1";

const Dropdown = ({
  label,
  items,
  type,
  value,
  onChange,
  isProjectDropdown,
  index,
  hintText,
  countryValue,
  required = false,
  errorMessage = "This field is required",
  onSave,
  isPreview = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);
  const [isTouched, setIsTouched] = useState(false);

  const validate = () => {
    if (!required) return true;
    
    if (type === "LANGUAGE") {
      return Array.isArray(value) && value.length > 0;
    } else if (type === "VALUE CHAIN") {
      return Array.isArray(value) && value.length > 0;
    } else {
      return value !== null && value !== undefined && value !== "";
    }
  };

  const isValid = validate();
  const showError = required && isTouched && !isValid;

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, items]);

  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  const isValueChain = type === "VALUE CHAIN";
  const isSMMEs = type === "SMMEs";
  const isLanguage = type === "LANGUAGE";
  const selectedValues = isValueChain
    ? Array.isArray(value)
      ? value
      : value
      ? [value]
      : []
    : value;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleButtonClick = () => {
    setIsTouched(true);
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isTouched) {
      validate();
    }
  }, [value, isTouched]);

  const handleItemClick = (item) => {
    setSelected(item);
    setIsTouched(true);
    if (isLanguage) {
      let newValues = Array.isArray(value) ? [...value] : [];
      if (newValues.includes(item)) {
        newValues = newValues.filter((val) => val !== item);
      } else {
        newValues.push(item);
      }
      onChange(type, newValues);
    }
    else if (isValueChain) {
      const alreadySelected = selectedValues.some(
        (v) => {
          if (typeof v === "object" && typeof item === "object") {
            return v.value === item.value;
          }
          return v === item;
        }
      );
      let newSelectedValues;
      if (alreadySelected) {
        newSelectedValues = selectedValues.filter(
          (v) =>
            !(
              typeof v === "object" &&
              typeof item === "object" &&
              v.value === item.value
            ) && v !== item
        );
      } else {
        newSelectedValues = [...selectedValues, item];
      }
      onChange(type, newSelectedValues);
    } else {
      onChange(type, item);
      setIsOpen(false);
    }
  };

  const [selected, setSelected] = useState(items[0]);

  const getDisplayValue = () => {
    if (!value && !isLanguage) return "-";
    
    if (isLanguage) {
      if (Array.isArray(value) && value.length > 0) {
        return value[0];
      }
      return "-";
    }
    
    if (isSMMEs && value && typeof value === "object") {
      const selectedSection = policyDropdownItems[type]?.find(section => 
        section.items.some(item => item.value === value.value)
      );
      return selectedSection?.heading || "-";
    }
    
    if (isValueChain) {
      if (Array.isArray(selectedValues) && selectedValues.length > 0) {
        const lastSelected = selectedValues[selectedValues.length - 1];
        const selectedSection = policyDropdownItems[type]?.find(section => 
          section.items.some(item => 
            (typeof lastSelected === "object" && item.value === lastSelected.value) ||
            item.value === lastSelected
          )
        );
        return selectedSection?.heading || 
          (typeof lastSelected === "object" ? lastSelected.label || lastSelected.name : lastSelected) || 
          "-";
      }
      return "-";
    }
    
    if (typeof value === "object" && value !== null) {
      const selectedSection = policyDropdownItems[type]?.find(section => 
        section.items.some(item => item.value === value.value)
      );
      return selectedSection?.heading || value.label || value.name || "-";
    }
    
    return value || "-";
  };

  const renderGroupedItems = () => {
    if (!policyDropdownItems[type] || !Array.isArray(policyDropdownItems[type])) return null;
  
    return policyDropdownItems[type].map((section, sectionIndex) => (
      <React.Fragment key={sectionIndex}>
        <li className="text-[4px] md:text-[12px] font-bold bg-gray-100 px-1md:px-3 py-1 sticky top-0 z-50">
          {section.heading}
        </li>
        {section.items.map((item, itemIndex) => {
          const isSelected = 
            (Array.isArray(value) && value.includes(item.value) || 
            value?.value === item.value);

          return (
            <li
              key={`${sectionIndex}-${itemIndex}`}
              onMouseEnter={() => setHoveredItem(item.label)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleItemClick(item);
              }}
              className="flex justify-between items-center px-[3px] md:px-3 py-1 md:py-2 rounded-md hover:bg-gray-100 cursor-pointer relative"
            >
              <span className="text-black text-[4px] md:text-[12px] flex-1 mr-2 whitespace-normal break-words">
                {item.label}
              </span>
              <div className="flex items-center">
                <div
                  className={`w-2 h-2 md:w-4 md:h-4 rounded-full ${
                    isSelected ? "bg-[#2E453D]" : "bg-gray-200"
                  }`}
                ></div>
              </div>
            </li>
          );
        })}
      </React.Fragment>
    ));
  };

  function capitalizeWords(str) {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }
  
  return (
    <div
      className="flex flex-col w-full md:mt-0 mt-1 relative md:mb-3"
      style={{ position: "relative", zIndex: isOpen ? 100 : 1 }}
      ref={dropdownRef}
    >
      <label className="md:text-[20px] font-bold   text-[4px] text-black mb-1 capitalize ">
        {label}
      </label>
      {showError && (
        <span className="text-red-500 text-[3px] md:text-[12px]">
          {errorMessage}
        </span>
      )}
      <div
        className="flex relative z-50 w-full"
        ref={dropdownButtonRef}
        onClick={handleButtonClick}
      >
        <button
          className={`relative xl:w-[168px] lg:w-[140px] md:w-[100px] w-full h-[20px] md:h-[53px] ${
            isProjectDropdown ? "bg-[#2E453D]" : "bg-[#2E453D]"
          } text-white flex justify-between items-center text-[4px] md:text-[17px] px-[2px] rounded-l-[4px] truncate`}
        >
          <span className={`whitespace-normal break-words font-bold leading-[0.90] text-center capitalize w-full ${
            getDisplayValue() === "-" ? "text-gray-400" : ""
          }`}>
            {capitalizeWords(getDisplayValue())}
          </span>
          <span className="absolute bottom-[1px] right-3 text-[3px] md:text-[8px] text-white">
            {selected?.code || selected?.value || '-'}
          </span>
        </button>
        <div
          className={`w-[16px] md:w-[37px] h-[20px] md:h-[53px] ${
            isProjectDropdown ? "bg-[#152C24]" : "bg-[#152C24]"
          } flex justify-center items-center rounded-r-[4px] -ml-1 z-50`}
          onClick={handleButtonClick}
        >
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745014174/Expand_down_3_ggzyur.png"
            alt="expand"
            className="w-2 h-2 md:w-5 md:h-[24px]"
          />
        </div>
      </div>
      <p className={`text-[4px] md:text-[14px] mt-[2px] truncate ${
        showError ? "text-red-500" : "text-black"
      }`}>
        {showError ? errorMessage : ""}
      </p>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownMenuRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full mt-1 left-0 w-full md:w-[190px] border border-[#979797]/80 bg-white rounded shadow-lg p-[0px] md:p-2 max-h-[160px] md:max-h-[400px] flex flex-col"
            style={{
              position: "absolute",
              zIndex: 1000,
              backgroundColor: "white",
            }}
          >
            <li className="flex items-center justify-between text-[4px] px-1 md:px-3 pt-1 md:text-[14px] font-semibold md:mb-2">
              {label}
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750034880/Export_1_vurzim.png"
                alt="icon"
                className="md:w-[30px] md:h-[30px] h-[8px] w-[8px] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
              />
            </li>

            <div className="w-full h-5 flex justify-end">
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png"
                alt="scroll-up"
                className="md:w-4 md:h-4 h-2 w-2"
                onClick={() => {
                  contentRef.current.scrollBy({
                    top: -50,
                    behavior: "smooth",
                  });
                }}
              />
            </div>

            <div className="custom-scroll-area">
              <div
                className="custom-scroll-content"
                ref={contentRef}
                onScroll={handleScroll}
              >
                <ul className="md:px-2">
                  {renderGroupedItems()}
                </ul>
              </div>
              <div className="custom-scroll-track" onClick={handleTrackClick}>
                <div
                  ref={thumbRef}
                  className="custom-scroll-thumb"
                  style={{
                    height: `${thumbHeight}px`,
                    top: `${scrollTop}px`,
                    cursor: isDragging ? "grabbing" : "grab",
                  }}
                  onMouseDown={handleThumbMouseDown}
                ></div>
              </div>
            </div>

            <div className="w-full h-5 flex justify-end">
              <img
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png"
                alt="scroll-down"
                className="md:w-4 md:h-4 h-2 w-2"
                onClick={() => {
                  contentRef.current.scrollBy({
                    top: 50,
                    behavior: "smooth",
                  });
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-[4px] md:text-[14px] mt-[2px] text-black truncate">
        {hintText}
      </p>

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 200px;
          display: flex;
          overflow: hidden;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 1px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 14px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
          .custom-scroll-area {
            position: relative;
            max-height: 150px;
            display: flex;
            overflow: hidden;
            width: 100%;
          }
          .custom-scroll-content {
            flex: 1;
            overflow-y: scroll;
            scrollbar-width: none;
            -ms-overflow-style: none;
            padding-right: 0px;
          }
        }
      `}</style>
    </div>
  );
};

const ReportsSection = ({ values, onChange, isPreview = false, onSave }) => {
  const policyTypes = Object.keys(policyDropdownItems);
  const { selectedCountry } = useCountry();

  useEffect(() => {
    if (selectedCountry && selectedCountry.name !== values.COUNTRY?.name) {
      onChange('COUNTRY', selectedCountry);
    }
  }, [selectedCountry, onChange]);

  const hintTexts = {
    COUNTRY: "Select country",
    REGION: "Select region / province / Country",
    DISTRICT: "Select District / Constituency Municipality",
    SMMEs: "Select type of framing",
    "TYPE OF FARMING": "Select type of produce",
    "USER TYPE": "Select type of produce",
  };

  const firstRow = policyTypes.slice(0, 5);
  const secondRow = policyTypes.slice(5, 10);

  return (
    <div className="w-full md:mb-10 md:mt-10 mt-5 mb-3 font-Inter mx-auto px-1">
      <div className="flex  gap-[2px] md:gap-[3px] w-full mb-2">
        {firstRow.map((type) => (
          <div key={type}  className="w-[16.6%] min-w-[65px] md:w-auto">
            {isPreview ? (
              <div className="flex flex-col">
                <label className={`md:text-[10px] font-bold text-[5px] text-black mb-1 capitalize ${
                  type.split(' ').length >= 3 ? 'whitespace-pre-line' : ''
                }`}>
                  {type}
                </label>
                <div className="flex relative z-50 w-full">
                  <button
                    className={`relative xl:w-[168px] lg:w-[140px] md:w-[100px] w-full h-[20px] md:h-[53px] bg-[#2E453D] text-white flex justify-between items-center text-[4px] md:text-[17px] px-[2px] rounded-l-[4px] `}
                  >
                    <span className={`whitespace-normal break-words font-bold leading-[0.90] text-center capitalize w-full`}>
                      {values[type]?.label || values[type]?.name || values[type] || "-"}
                    </span>
                    {values[type]?.value && (
                      <span className="absolute bottom-[1px] right-3 text-[3px] md:text-[8px] text-white">
                        {values[type].value}
                      </span>
                    )}
                  </button>
                  <div
                    className={`w-[16px] md:w-[37px] h-[20px] md:h-[53px] bg-[#152C24] flex justify-center items-center rounded-r-[4px] -ml-1 z-30`}
                  >
                  
                  </div>
                </div>
              </div>
            ) : (
              <Dropdown
                label={type}
                items={policyDropdownItems[type]}
                type={type}
                value={values[type]}
                onChange={onChange}
                countryValue={values.COUNTRY}
                hintText={hintTexts[type] || "Select an option"}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-[2px] md:gap-[10px] md:mt-10 w-full">
        {secondRow.map((type) => (
          <div key={type}  className="w-[16.6%] min-w-[65px] md:w-auto">
            {isPreview ? (
              <div className="flex flex-col">
                <label className={`md:text-[10px] font-bold text-[5px] text-black mb-1 capitalize ${
                  type.split(' ').length >= 3 ? 'whitespace-pre-line' : ''
                }`}>
                  {type}
                </label>
                <div className="flex relative z-20 w-full">
                  <button
                    className={`relative xl:w-[168px] lg:w-[140px] md:w-[100px] w-full h-[20px] md:h-[53px] bg-[#2E453D] text-white flex justify-between items-center text-[4px] md:text-[17px] px-[2px] rounded-l-[4px] `}
                  >
                    <span className={`whitespace-normal break-words font-bold leading-[0.90] text-center capitalize w-full`}>
                      {values[type]?.label || values[type]?.name || values[type] || "-"}
                    </span>
                    {values[type]?.value && (
                      <span className="absolute bottom-[1px] right-3 text-[3px] md:text-[8px] text-white">
                        {values[type].value}
                      </span>
                    )}
                  </button>
                  <div
                    className={`w-[16px] md:w-[37px] h-[20px] md:h-[53px] bg-[#152C24] flex justify-center items-center rounded-r-[4px] -ml-1 z-30`}
                  >
                    
                  </div>
                </div>
              </div>
            ) : (
              <Dropdown
                label={type}
                items={policyDropdownItems[type]}
                type={type}
                value={values[type]}
                onChange={onChange}
                countryValue={values.COUNTRY}
                hintText={hintTexts[type] || "Select an option"}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportsSection;